import React, { useState, useEffect,useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ClockIcon, 
  ArrowDownTrayIcon,
  InformationCircleIcon,
  ChevronLeftIcon,
  CodeBracketSquareIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getStageTwo, submitStageTwo } from '../../api/userApi';
import Toast from '../../components/ui/Toast';

const StageTwo = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  const [formData, setFormData] = useState({ github_url: '', live_url: '' });

  useEffect(() => {
    const loadStageData = async () => {
  try {
    const { data } = await getStageTwo(competitionId);

    setAssignment(data.assignment);
    setStartedAt(data.startedAt);

  } catch (err) {
    navigate(`/dashboard/competition-hub/${competitionId}`);
  } finally {
    setLoading(false);
  }
};
    loadStageData();
  }, [competitionId, navigate]);

  // TIMER LOGIC: Calculates diff every second
    useEffect(() => {
  if (!startedAt || assignment?.status === "submitted" || assignment?.status === "completed") {
    return;
  }

    const calculateTime = () => {
      // 1. Ensure startedAt is a valid Date object
      const startTime = new Date(startedAt).getTime();
      
      // 2. Set the duration (24 Hours)
      const durationMs = 24 * 60 * 60 * 1000;
      const targetTime = startTime + durationMs;
      
      const now = new Date().getTime();
      const diff = targetTime - now;

      // Debugging: If the timer isn't moving, check these in your console
      

      if (diff <= 0) {
        setTimeLeft(0);
        setIsExpired(true);
        return false;
      }
      
      setTimeLeft(diff);
      return true;
    };

    // Run once immediately
    calculateTime();

    const timer = setInterval(() => {
      const active = calculateTime();
      if (!active) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [startedAt,assignment?.status]);

    // ================================
// PARSE ASSIGNMENT DESCRIPTION
// ================================
const parseAssignment = (text) => {
  if (!text) return { topic: "", tasks: [], evaluation: [] };

  const sections = {
    topic: "",
    tasks: [],
    evaluation: []
  };

  const lines = text.split("\n");

  let currentSection = "";

  lines.forEach(line => {
    const clean = line.trim();

    if (clean.toLowerCase().includes("project topic")) {
      currentSection = "topic";
      return;
    }

    if (clean.toLowerCase().includes("tasks")) {
      currentSection = "tasks";
      return;
    }

    if (clean.toLowerCase().includes("evaluation")) {
      currentSection = "evaluation";
      return;
    }

    if (!clean) return;

    if (currentSection === "topic") {
      sections.topic += clean + " ";
    }

    if (currentSection === "tasks") {
      sections.tasks.push(clean);
    }

    if (currentSection === "evaluation") {
      sections.evaluation.push(clean);
    }
  });

  return sections;
};



const downloadAssignment = async () => {
  if (!assignment) return;

  const doc = new jsPDF("p", "pt", "a4");

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 50;

  // 🔥 LOAD FONT FROM PUBLIC FOLDER
  const response = await fetch("/fonts/NotoSans-Regular.ttf");
  const fontBuffer = await response.arrayBuffer();

  const fontBase64 = btoa(
    new Uint8Array(fontBuffer).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      ""
    )
  );

  doc.addFileToVFS("NotoSans-Regular.ttf", fontBase64);
  doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
  doc.setFont("NotoSans");

  // Decode HTML safely
  const decodeText = (text) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };
  


  // ================= HEADER =================
  doc.setFontSize(18);
  doc.text("OFFICIAL MISSION BRIEF", pageWidth / 2, 60, { align: "center" });

  doc.setFontSize(13);
  doc.setTextColor(90);
  doc.text(
    assignment?.title || "UNNAMED MISSION",
    pageWidth / 2,
    90,
    { align: "center" }
  );

  doc.setDrawColor(200);
  doc.line(margin, 105, pageWidth - margin, 105);

  // ================= BODY =================
  autoTable(doc, {
    startY: 130,
    margin: { left: margin, right: margin },
    theme: "plain",
    styles: {
      font: "NotoSans", // 🔥 VERY IMPORTANT
      fontSize: 11,
      cellPadding: 4,
    },
body: [
  [{ content: "PROJECT TOPIC", styles: { fontStyle: "bold" } }],
 [{ content: parsedAssignment.topic || "No topic provided." }],

  [{ content: "TASKS", styles: { fontStyle: "bold" } }],
  [{ content: parsedAssignment.tasks.length ? parsedAssignment.tasks.join("\n") : "No tasks specified." }],

  [{ content: "EVALUATION CRITERIA", styles: { fontStyle: "bold" } }],
  [{ content: parsedAssignment.evaluation.length ? parsedAssignment.evaluation.join("\n") : "No evaluation criteria provided." }],
],
   
  });
  


  // ================= FOOTER =================
  const pageCount = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - margin,
      pageHeight - 30,
      { align: "right" }
    );
  }

  doc.save(`Mission_Brief_${competitionId.slice(0, 8)}.pdf`);
};

  const formatTime = (ms) => {
    if (ms === null) return "00:00:00";
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isExpired) return;

    if (assignment?.status === "submitted") {
  setNotification({
    msg: "Assignment already submitted.",
    type: "info"
  });
  return;
}

    // Final confirmation
  const confirmSubmit = window.confirm("Are you sure? You cannot edit your links after submission.");
  if (!confirmSubmit) return;

    setIsSubmitting(true);
    try {
      await submitStageTwo({ competitionId, ...formData });

setAssignment((prev) => ({
  ...prev,
  status: "submitted"
}));

setNotification({ 
  msg: "Project submitted successfully.", 
  type: "success" 
});
      setTimeout(() => navigate(`/dashboard/competition-hub/${competitionId}`), 2000);
    } catch (err) {
      setNotification({ msg: "Something is wrong. Try again!", type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };



// Parse assignment safely
const parsedAssignment = useMemo(() => {
  return parseAssignment(assignment?.description);
}, [assignment?.description]);




  useEffect(() => {
  if (assignment?.status === "submitted" || assignment?.status === "completed") {
    setNotification({
      msg: "You have already submitted your project.",
      type: "success"
    });
  }
}, [assignment]);




  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c] text-blue-500 font-mono animate-pulse">ESTABLISHING SECURE LINK...</div>;

 
  // Inside your component, above the return:
if (assignment?.status === 'completed' || assignment?.status === 'submitted') {
  return (
    <div className="min-h-screen bg-[#0a0a0c] flex flex-col items-center justify-center p-6 text-center">
      <div className="bg-green-500/10 border border-green-500/20 p-10 rounded-3xl max-w-md">
        <div className="h-16 w-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(34,197,94,0.3)]">
          <CheckCircleIcon className="h-10 w-10 text-white" />
        </div>
        <h2 className="text-2xl font-black text-white uppercase tracking-tighter mb-4">Successfully Submitted</h2>
        <p className="text-gray-400 text-sm leading-relaxed mb-8">
          Your project has been received. Our team is now reviewing your source code. 
          Check the <span className="text-blue-500">Notice Board</span> for your interview slot.
        </p>
        <button 
          onClick={() => navigate(`/dashboard/competition-hub/${competitionId}`)}
          className="w-full py-4 bg-white text-black font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-gray-200 transition-all"
        >
          Return to Hub
        </button>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-gray-400 font-sans pb-12">
      {/* Navbar */}
      <nav className="border-b border-gray-900 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button onClick={() => navigate(`/dashboard/competition-hub/${competitionId}`)} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest hover:text-white transition-all">
            <ChevronLeftIcon className="h-4 w-4" /> Back TO HUB
          </button>
          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2 bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20">
                <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                <span className="text-[9px] font-mono text-blue-400 uppercase tracking-tighter">Live Connection</span>
             </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 pt-10 space-y-8">
        {notification && <Toast message={notification.msg} type={notification.type} onClose={() => setNotification(null)} />}

        {/* 1. GUIDELINES PANEL */}
        <section className="bg-[#111114] border border-gray-800 rounded-2xl overflow-hidden shadow-2xl">
           <div className="bg-gray-800/30 px-6 py-3 border-b border-gray-800 flex items-center gap-2">
              <InformationCircleIcon className="h-4 w-4 text-blue-500" />
              <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-white">Read The Guidelines</h2>
           </div>
           <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {[
                "Submit within the 24-hour window. Late entries are auto-locked.",
                "AI usage is permitted for speed; core logic mastery is mandatory.",
                "After completing your project, deploy it to GitHub, come back to this page, and submit the repository link (create a GitHub account if you don't have one).",
                "Once the final project is submitted, a live meeting will be conducted. Winners will be evaluated based on their performance in this session.",
                "Download the PDF brief for offline development reference.",
                "If you find any issues, please contact us at genzevaluation@gmail.com",
                "All the best! You can do this."
              ].map((note, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="text-blue-600 font-mono text-xs">0{i+1}</span>
                  <p className="text-[12px] leading-tight">{note}</p>
                </div>
              ))}
           </div>
        </section>

        {/* 2. CORE INTERFACE */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Mission Briefing */}
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-[#111114] border border-gray-800 rounded-3xl p-10 shadow-inner">
              <header className="text-center mb-8 border-b border-gray-800/50 pb-8">
                <span className="text-blue-500 font-mono text-[9px] uppercase tracking-[0.5em] block mb-2">Project Core Idea</span>
                {/* REDUCED TITLE SIZE */}
                <h1 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight max-w-2xl mx-auto leading-tight">
                  {assignment?.title}
                </h1>
              </header>
              
              <div className="prose prose-invert max-w-none">
                <div className="h-[400px] overflow-y-auto pr-4 custom-scrollbar text-sm leading-relaxed text-gray-400 whitespace-pre-line font-medium italic">
                <div className="space-y-6 text-gray-300">

  {/* PROJECT TOPIC */}
  <div>
    <h3 className="text-sm font-black text-blue-500 uppercase mb-2">
      Project Topic
    </h3>
    <p className="text-sm leading-relaxed">
      {parsedAssignment.topic || "Not specified"}
    </p>
  </div>

  {/* TASKS */}
  <div>
    <h3 className="text-sm font-black text-blue-500 uppercase mb-2">
      Tasks
    </h3>

    <ul className="space-y-2 text-sm">
{parsedAssignment.tasks.length > 0 ? (
  parsedAssignment.tasks.map((task, index) => (
    <li key={index} className="flex gap-3">
      <span className="text-blue-500 font-mono">
        {index + 1}.
      </span>
      <span>{task}</span>
    </li>
  ))
) : (
  <p className="text-gray-500 text-sm">No tasks specified.</p>
)}
    </ul>
  </div>

  {/* EVALUATION */}
  <div>
    <h3 className="text-sm font-black text-blue-500 uppercase mb-2">
      Evaluation Criteria
    </h3>

    <ul className="space-y-2 text-sm">
      {parsedAssignment.evaluation.map((item, index) => (
        <li key={index} className="flex gap-3">
          <span className="text-green-500">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>

</div>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-gray-800 flex justify-center">
                <button 
                  onClick={downloadAssignment}
                  className="group flex items-center gap-3 px-8 py-3 bg-white/5 hover:bg-blue-600 hover:text-white border border-white/10 rounded-xl transition-all duration-300"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-blue-500 group-hover:text-white" />
                  <span className="text-[10px] font-black uppercase tracking-widest">Download Project Idea</span>
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar: Status & Control */}
          <div className="lg:col-span-4 space-y-6 sticky top-24">
            
            {/* Timer Card */}
            <div className={`p-8 rounded-3xl border transition-colors duration-500 ${isExpired ? 'border-red-900 bg-red-900/10' : 'border-gray-800 bg-[#111114] shadow-2xl'}`}>
              <div className="flex justify-between items-center mb-4">
                 <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">Time to Lockdown</span>
                 <ClockIcon className={`h-4 w-4 ${isExpired ? 'text-red-500' : 'text-blue-500'}`} />
              </div>
              <div className={`text-5xl font-mono font-bold tracking-tighter text-center ${isExpired ? 'text-red-500' : 'text-white'}`}>
                {formatTime(timeLeft)}
              </div>
            </div>

            {/* Submission Form */}
            <div className="bg-[#111114] border border-gray-800 p-8 rounded-3xl">
              <h3 className="text-[10px] font-black text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                <CodeBracketSquareIcon className="h-4 w-4 text-blue-500" />
                Submit your Project link here
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Source Code link (GitHub)</label>
                  <input 
                    type="url" required 
                    disabled={isExpired || assignment?.status === "submitted"}
                    placeholder="https://github.com/..."
                    className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-white text-xs focus:border-blue-500 outline-none transition-all placeholder:opacity-20"
                    value={formData.github_url}
                    onChange={(e) => setFormData({...formData, github_url: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Project live link (Optional)</label>
                  <input 
                    type="url" 
                    disabled={isExpired || assignment?.status === "submitted"}
                    placeholder="https://..."
                    className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-white text-xs focus:border-blue-500 outline-none transition-all placeholder:opacity-20"
                    value={formData.live_url}
                    onChange={(e) => setFormData({...formData, live_url: e.target.value})}
                  />
                </div>

                <button 
                  type="submit"
                 
                  disabled={
                      isSubmitting || 
                              isExpired || 
                           assignment?.status === "submitted"
                            }
                  className={`w-full py-4 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl transition-all
                    ${isExpired ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 text-white active:scale-95'}
                  `}
                >
                  {isSubmitting ? 'Syncing...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default StageTwo;